import { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { useShallow } from 'zustand/react/shallow';

import SocketService from '@src/services/SocketService';

import { useCalculatorStore } from '@src/stores';
import { useTabsScrollControls } from '@src/features/calculator/hooks';
import { useCurrencyListQuery, useExchangeRatesQuery } from '@src/hooks/queries/useCurrencyQuery';

import { TConverterTab, IConversionResult, ECalculatorSocketEvents } from '@src/types';
import { converterTabs, converterUnitsMap } from '@src/features/calculator/constants';
import { convertWithMathjs, formatNumber, formatNumberWithCommas } from '@src/features/calculator/utils';

export const useConverter = (value: string, isReadOnly: boolean) => {
  const [openTop, setOpenTop] = useState(false);
  const [openBottom, setOpenBottom] = useState(false);
  const [activeTab, setActiveTab] = useState<TConverterTab>('currency');
  const [selectedTopUnit, setSelectedTopUnit] = useState('USD');
  const [selectedBottomUnit, setSelectedBottomUnit] = useState('ILS');
  const [fieldsSwapped, setFieldsSwapped] = useState(false);

  const topAnchorRef = useRef<HTMLDivElement>(null);
  const bottomAnchorRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const sharedTabsScrollLeftRef = useRef(0);
  const topTabsScroll = useTabsScrollControls(sharedTabsScrollLeftRef);
  const bottomTabsScroll = useTabsScrollControls(sharedTabsScrollLeftRef);

  const {
    converterConfigs: { isOpen, isSharedByMe, settings },
    setCalculatorState,
  } = useCalculatorStore(
    useShallow((state) => ({
      converterConfigs: state.sharedCalculatorData,
      setCalculatorState: state.setCalculatorState,
    }))
  );

  const { data: currencyData } = useCurrencyListQuery();

  const currencyUnits = useMemo(() => {
    if (!currencyData?.currencies) return converterUnitsMap.currency || [];
    return Object.entries(currencyData.currencies).map(([code, name]) => ({
      code,
      name,
    }));
  }, [currencyData]);

  const currentData = useMemo(() => {
    return activeTab === 'currency' ? currencyUnits : converterUnitsMap[activeTab] || [];
  }, [activeTab, currencyUnits]);

  const shouldBroadcastOperations = useMemo(
    () => isSharedByMe && isOpen && !isReadOnly,
    [isSharedByMe, isOpen, isReadOnly]
  );

  const getConversionResult = useCallback(
    (
      inputValue: string,
      fromUnitCode: string,
      toUnitCode: string,
      category: TConverterTab,
      apiRate?: number
    ): IConversionResult => {
      const numValue = parseFloat(inputValue);
      if (isNaN(numValue) || inputValue === '') {
        return { convertedValue: 0, displayString: '', rate: undefined, result: undefined };
      }

      let converted: number | string = 0;
      let rate: number | undefined;
      let displayString = '';
      let result: string | undefined;

      const isScientific = (v: string) => /e[+-]?\d+/i.test(v);

      const formatConverted = (v: string) => (isScientific(v) ? v : formatNumberWithCommas(parseFloat(v)));

      if (category === 'currency') {
        if (apiRate === undefined) {
          return { convertedValue: 0, displayString: '', rate: undefined, result: undefined };
        }

        rate = apiRate;
        converted = formatNumber(numValue * rate);
        displayString = `${formatNumberWithCommas(numValue)} ${fromUnitCode}`;

        const formattedRate = rate.toFixed(4).replace(/\.?0+$/, '');
        result = `${formatConverted(String(converted))} ${toUnitCode} (Rate:${formattedRate})`;
      } else {
        const units = converterUnitsMap[category] || [];
        const fromUnit = units.find((u) => u.code === fromUnitCode);
        const toUnit = units.find((u) => u.code === toUnitCode);
        if (!fromUnit || !toUnit) {
          return { convertedValue: 0, displayString: '', rate: undefined, result: undefined };
        }

        const fromName = fromUnit.mathjsName ?? fromUnit.code;
        const toName = toUnit.mathjsName ?? toUnit.code;

        converted = convertWithMathjs(numValue, fromName, toName);
        displayString = `${formatNumberWithCommas(numValue)} ${fromUnitCode}`;
        result = `${formatConverted(String(converted))} ${toUnitCode}`;
      }

      return { convertedValue: converted, displayString, rate, result };
    },
    []
  );

  const sourceUnit = fieldsSwapped ? selectedBottomUnit : selectedTopUnit;
  const targetUnit = fieldsSwapped ? selectedTopUnit : selectedBottomUnit;

  const { data: exchangeRates } = useExchangeRatesQuery(sourceUnit, activeTab === 'currency');
  const currencyRate = exchangeRates?.[targetUnit];

  const { convertedValue, displayString, result } = useMemo(() => {
    const sameUnit = sourceUnit === targetUnit;
    const rate = sameUnit ? 1 : currencyRate;

    return getConversionResult(value, sourceUnit, targetUnit, activeTab, rate);
  }, [getConversionResult, value, activeTab, sourceUnit, targetUnit, currencyRate]);

  useEffect(() => {
    setCalculatorState({
      result,
      expression: displayString,
      error: null,
    });

    if (currentData.length > 0) {
      if (selectedTopUnit && !currentData.find((i) => i.code === selectedTopUnit)) {
        setSelectedTopUnit(currentData[0].code);
      }
      if (selectedBottomUnit && !currentData.find((i) => i.code === selectedBottomUnit)) {
        setSelectedBottomUnit(currentData.length > 1 ? currentData[1].code : currentData[0].code);
      }
    }
  }, [displayString, result, setCalculatorState, currentData, selectedTopUnit, selectedBottomUnit]);

  useEffect(() => {
    if (!shouldBroadcastOperations) return;

    SocketService.emit(ECalculatorSocketEvents.UPDATE_CALCULATOR, {
      calculatorDetails: {
        converterConfigs: {
          selectedTopUnit,
          selectedBottomUnit,
          category: activeTab,
          swapped: fieldsSwapped,
        },
      },
    });
  }, [activeTab, selectedBottomUnit, selectedTopUnit, fieldsSwapped, shouldBroadcastOperations]);

  useEffect(() => {
    const {
      selectedTopUnit: initTop,
      selectedBottomUnit: initBottom,
      category: initCategory,
      swapped: initSwapped,
    } = settings?.converterConfigs ?? {};

    if (initTop) {
      setSelectedTopUnit(initTop);
    }
    if (initBottom) {
      setSelectedBottomUnit(initBottom);
    }
    if (initCategory) {
      setActiveTab(initCategory);
    }
    if (typeof initSwapped === 'boolean') {
      setFieldsSwapped(initSwapped);
    }
  }, [settings?.converterConfigs]);

  return {
    openTop,
    openBottom,
    activeTab,
    currentData,
    convertedValue,
    fieldsSwapped,
    selectedTopUnit,
    selectedBottomUnit,
    converterTabs,
    containerRef,
    topAnchorRef,
    bottomAnchorRef,
    topTabsScroll,
    bottomTabsScroll,
    setOpenTop,
    setOpenBottom,
    setActiveTab,
    setFieldsSwapped,
    setSelectedTopUnit,
    setSelectedBottomUnit,
  };
};
